import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React, {useEffect, useMemo} from 'react';

import {ErrorBadgeError, ErrorBadgePanel} from 'procedures/design/error-panel/ErrorBadge';
import {getMethodNameOrPlaceholder} from 'procedures/design/procedureMethodNodeCommonConsts';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {trackViewedProcedureValidationErrorsPanel} from 'procedures/procedureAnalyticsEventsUtil';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {RED, RED_LIGHTEST} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {
  buildProcedureValidationErrorsForPanel,
  buildValidationErrorsForPanelFromProcessFlowchartValidationErrors,
} from '../validation/procedureValidationUtils';

interface BaseProcedureErrorToolTipProps {
  nodeCidToNodeNameMap: Record<string, string>;
}

interface ProcedureErrorToolTipPropsLegacy extends BaseProcedureErrorToolTipProps {
  isProcedurePoW: false;
  processFlowchartValidationErrors?: never;
}

interface ProcedureErrorToolTipPropsPoW extends BaseProcedureErrorToolTipProps {
  isProcedurePoW: true;
  // Validation errors for PoW procedures
  processFlowchartValidationErrors: Array<ProcessFlowchartValidationError>;
}

type ProcedureErrorToolTipProps = ProcedureErrorToolTipPropsPoW | ProcedureErrorToolTipPropsLegacy;

interface BaseProcedureErrorPanelProps {
  nodeCidToNodeNameMap: Record<string, string>;
}

interface ProcedureErrorPanelPropsLegacy extends BaseProcedureErrorPanelProps {
  isProcedurePoW: false;
  processFlowchartValidationErrors?: never;
}

interface ProcedureErrorPanelPropsPoW extends BaseProcedureErrorPanelProps {
  isProcedurePoW: true;
  processFlowchartValidationErrors: Array<ProcessFlowchartValidationError>;
}

type ProcedureErrorPanelProps = ProcedureErrorPanelPropsPoW | ProcedureErrorPanelPropsLegacy;

function ProcedureErrorPanel(props: ProcedureErrorPanelProps): JSX.Element {
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();
  const {procedureMetadata} = ProcedurePageContext.useContext();
  const procedureLabel = getProcedureUIString(
    ProceduresStringKeys.PROCEDURE,
    procedureMetadata.procedureType,
    {
      stringCase: StringCase.LOWER,
    }
  );

  useEffect(() => {
    trackViewedProcedureValidationErrorsPanel(procedureMetadata.id, procedureMetadata.procedureType);
  }, [procedureMetadata.id, procedureMetadata.procedureType]);

  const resolvedMethodNameMap = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(props.nodeCidToNodeNameMap).map(([cid, name]) => [
          cid,
          getMethodNameOrPlaceholder(name, procedureMetadata.procedureType),
        ])
      ),
    [props.nodeCidToNodeNameMap, procedureMetadata.procedureType]
  );

  const errorsForPanel = useMemo(
    () =>
      props.isProcedurePoW
        ? buildValidationErrorsForPanelFromProcessFlowchartValidationErrors(
            props.processFlowchartValidationErrors,
            props.nodeCidToNodeNameMap
          )
        : buildProcedureValidationErrorsForPanel(procedureValidationErrors),
    [
      props.isProcedurePoW,
      props.processFlowchartValidationErrors,
      procedureValidationErrors,
      props.nodeCidToNodeNameMap,
    ]
  );

  const errors: Array<ErrorBadgeError> = useMemo(() => {
    const result: Array<ErrorBadgeError> = [];
    for (const message of Object.keys(errorsForPanel.flowchartErrorCounts)) {
      result.push({message, count: errorsForPanel.flowchartErrorCounts[message]});
    }
    for (const methodCid of Object.keys(errorsForPanel.methodErrorCountsByMethodCid)) {
      const category = resolvedMethodNameMap[methodCid] ?? '';
      assert(category !== '', `Expected method ${methodCid} to have a name`);
      const errorCounts = errorsForPanel.methodErrorCountsByMethodCid[methodCid];
      for (const message of Object.keys(errorCounts)) {
        result.push({message, category, count: errorCounts[message]});
      }
    }
    return result;
  }, [errorsForPanel, resolvedMethodNameMap]);

  return (
    <ErrorBadgePanel
      errors={errors}
      title={`Resolve errors to use ${procedureLabel}`}
      subtitle={`Resolve all errors to use your ${procedureLabel} in a study and plan experiments.`}
    />
  );
}

export default function ProcedureErrorToolTip(props: ProcedureErrorToolTipProps): JSX.Element {
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();

  const validationErrors = props.isProcedurePoW
    ? props.processFlowchartValidationErrors
    : procedureValidationErrors;

  const panelProps: ProcedureErrorPanelProps = props.isProcedurePoW
    ? {
        isProcedurePoW: true,
        processFlowchartValidationErrors: props.processFlowchartValidationErrors,
        nodeCidToNodeNameMap: props.nodeCidToNodeNameMap,
      }
    : {
        isProcedurePoW: false,
        nodeCidToNodeNameMap: props.nodeCidToNodeNameMap,
      };

  return validationErrors.length > 0 ? (
    <Popover
      placement={FloatingPlacement.BOTTOM}
      renderContent={() => <ProcedureErrorPanel {...panelProps} />}
    >
      <div className={css(styles.errorStatusBadge)}>
        {validationErrors.length}
        <BenchlingIcon className={css(styles.validationErrorIcon)} icon="exclamation-circle" />
      </div>
    </Popover>
  ) : (
    <></>
  );
}

const styles = StyleSheet.create({
  errorStatusBadge: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px',
    margin: '4px',
    gap: '8px',
    height: '28px',
    background: RED_LIGHTEST,
    borderRadius: '4px',
  },
  validationErrorIcon: {
    color: RED,
    width: '15px',
    height: '15px',
  },
});
